import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { PlatePair } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { getPlateSizeString, getPlateTypeString } from '@paragon/material-lists';
import { naturalSortByProperty } from '@paragon/utilities';

import { RootState } from '../../../../store';
import { updateAndPotentiallyCommitInsertionPlatePair } from '../../../../store/actions/insertActions';
import {
  getPlateTypesInInventory,
  getUniquePlatesInMaterialSetForActiveComponentDesign,
} from '../../../../store/selectors/getUniquePlatesInPlateList';
import { InsertingPlatePairInfo } from '../../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { getPlateDimensionOptions, PlateDimensions } from '../properties/PlatePairProperties';
import InsertionPanelContainer from './InsertionPanelContainer';
import { PositionField } from './PositionField';

function getInsertionPlatePair({ application: { insertingObjectInfo } }: RootState): InsertingPlatePairInfo | null {
  return insertingObjectInfo?.type === 'platePair' ? insertingObjectInfo : null;
}

export default function PlatePairInsertionSubPanel() {
  const dispatch = useAppDispatch();

  const platePair = useAppSelector(getInsertionPlatePair);

  const inventoryPlates = useAppSelector(getUniquePlatesInMaterialSetForActiveComponentDesign);
  const plateTypes = useAppSelector(getPlateTypesInInventory);

  const plateDimensionOptions =
    !!platePair?.plateType && !!platePair.tangiblePlateTypesForComboPlateType
      ? getPlateDimensionOptions(inventoryPlates, {
          plateType: platePair.plateType,
          tangiblePlateTypesForComboPlateType: platePair.tangiblePlateTypesForComboPlateType,
        })
      : [];

  return (
    <InsertionPanelContainer hideSubmitButton>
      <PropertiesPanel title="Plate Insertion">
        <Box sx={{ m: 1 }}>
          <Box sx={{ m: 1, display: 'flex', justifyContent: 'space-between' }}>
            <PropertySelectField<Pick<PlatePair, 'plateType' | 'tangiblePlateTypesForComboPlateType'>>
              sx={{ m: 1, width: '50%' }}
              value={
                platePair?.plateType && platePair.tangiblePlateTypesForComboPlateType
                  ? {
                      plateType: platePair.plateType,
                      tangiblePlateTypesForComboPlateType: platePair.tangiblePlateTypesForComboPlateType,
                    }
                  : null
              }
              getValue={({ plateType, tangiblePlateTypesForComboPlateType }) =>
                getPlateTypeString(plateType, tangiblePlateTypesForComboPlateType)
              }
              getName={({ plateType, tangiblePlateTypesForComboPlateType }) =>
                getPlateTypeString(plateType, tangiblePlateTypesForComboPlateType)
              }
              property="Plate Type"
              submit={({ plateType, tangiblePlateTypesForComboPlateType }) => {
                dispatch(
                  updateAndPotentiallyCommitInsertionPlatePair({ plateType, tangiblePlateTypesForComboPlateType }),
                );
              }}
              options={naturalSortByProperty(plateTypes, ({ plateType, tangiblePlateTypesForComboPlateType }) =>
                getPlateTypeString(plateType, tangiblePlateTypesForComboPlateType),
              )}
            />
            <PropertySelectField<PlateDimensions>
              sx={{ m: 1, width: '50%' }}
              value={platePair?.plateDimensions ?? null}
              disabled={platePair?.plateType == null}
              property={`Plate Dimensions${platePair?.plateType ? '' : ' (select plate type first)'}`}
              submit={(plateDimensions) => {
                dispatch(updateAndPotentiallyCommitInsertionPlatePair({ plateDimensions }));
              }}
              options={plateDimensionOptions}
              getName={(plateDimensions) => getPlateSizeString(plateDimensions, true)}
              getValue={(plateDimensions) => getPlateSizeString(plateDimensions, true)}
            />
            <ToggleButtonGroup
              value={platePair?.platePlacement}
              exclusive
              onChange={(_, platePlacement) => {
                if (platePlacement != null) dispatch(updateAndPotentiallyCommitInsertionPlatePair({ platePlacement }));
              }}
            >
              <ToggleButton value="FrontBack">Front/Back</ToggleButton>
              <ToggleButton value="TopBottom">Top/Bottom</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ m: 2, display: 'flex', justifyContent: 'center' }}>
            <PositionField
              position={platePair?.position ?? null}
              labelAdjective="Plate"
              placeholderText="Select a joint to apply the plate with the selected options"
            />
          </Box>
        </Box>
      </PropertiesPanel>
    </InsertionPanelContainer>
  );
}
